// Single source of truth for permanently moved URLs (old path -> new path).
//
// Consumed by BOTH:
//   - scripts/prerender.ts: writes a static redirect stub (0s meta refresh +
//     canonical to the new URL) at each old path so crawlers and direct hits
//     transfer to the new URL. Old paths are excluded from sitemap.xml.
//   - src/App.tsx: registers client-side <Redirect> routes so in-app
//     navigation and stale links land on the new URL instantly.
//
// Background: the static host does not support server-side 301 redirects, so
// a 0-second meta refresh with a matching canonical is the strongest available
// permanent-move signal. Keep this module free of asset imports so it can run
// under plain Node/tsx (same rule as routes.ts).
//
// July 2026 restructure: service-area pages flattened to max 3 URL levels.
// Siding and gutter town pages moved from
//   /services/siding-contractor-lancaster-pa/<service>-<town>-pa/
// to
//   /service-areas/<service>-<town>-pa/
// and the gutter service hub moved up one level under /services/.
// Roofing town pages were already 3 levels deep and did NOT move.

import { towns } from "../data/towns";

export interface RedirectEntry {
  /** Old absolute path, beginning and ending with a slash. */
  from: string;
  /** New absolute path, beginning and ending with a slash. */
  to: string;
}

export const redirects: RedirectEntry[] = [
  // Aug 2026: roofing hub + all roofing sub-service pages moved under /services/.
  {
    from: "/roofing-company-lancaster-pa/",
    to: "/services/roofing-company-lancaster-pa/",
  },
  ...[
    "roof-repair-lancaster-pa",
    "roof-rejuvenation-lancaster-pa",
    "roofing-inspection-lancaster-pa",
    "free-roof-estimates-lancaster-pa",
    "metal-roofing-lancaster-pa",
    "rubber-roofing-installation-lancaster-pa",
    "shingle-roofing-lancaster-pa",
    "roofing-storm-damage-lancaster-pa",
  ].map((slug) => ({
    from: `/roofing-company-lancaster-pa/${slug}/`,
    to: `/services/${slug}/`,
  })),
  // Aug 2026: Service Areas hub moved to the clean top-level slug.
  {
    from: "/roofing-company-lancaster-pa/roofing-areas-served-in-pa/",
    to: "/service-areas/",
  },
  // Gutter service hub: 4 levels -> 3 levels.
  {
    from: "/services/siding-contractor-lancaster-pa/gutter-installation-lancaster-pa-york-pa/",
    to: "/services/gutter-installation-lancaster-pa-york-pa/",
  },
  // Siding + gutter town pages: 4 levels -> 3 levels under /service-areas/.
  ...towns.flatMap((t) => [
    {
      from: `/services/siding-contractor-lancaster-pa/siding-contractor-${t.slug}-pa/`,
      to: `/service-areas/siding-contractor-${t.slug}-pa/`,
    },
    {
      from: `/services/siding-contractor-lancaster-pa/gutter-installation-${t.slug}-pa/`,
      to: `/service-areas/gutter-installation-${t.slug}-pa/`,
    },
  ]),
  // July 2026: roofing town pages moved from under the roofing hub to /service-areas/.
  {
    from: "/roofing-company-lancaster-pa/roofing-contractor-lancaster-county-pa/",
    to: "/service-areas/roofing-contractor-lancaster-county-pa/",
  },
  ...towns.map((t) => ({
    from: `/roofing-company-lancaster-pa/roofing-contractor-${t.slug}-pa/`,
    to: `/service-areas/roofing-contractor-${t.slug}-pa/`,
  })),
  // August 2026: Merion Station town pages renamed to Lower Merion (the phrase
  // people actually search). Covers the current 3-level URLs plus the legacy
  // pre-restructure paths, all pointing directly at the new lower-merion URLs.
  {
    from: "/service-areas/roofing-contractor-merion-station-pa/",
    to: "/service-areas/roofing-contractor-lower-merion-pa/",
  },
  {
    from: "/service-areas/siding-contractor-merion-station-pa/",
    to: "/service-areas/siding-contractor-lower-merion-pa/",
  },
  {
    from: "/service-areas/gutter-installation-merion-station-pa/",
    to: "/service-areas/gutter-installation-lower-merion-pa/",
  },
  {
    from: "/roofing-company-lancaster-pa/roofing-contractor-merion-station-pa/",
    to: "/service-areas/roofing-contractor-lower-merion-pa/",
  },
  {
    from: "/services/siding-contractor-lancaster-pa/siding-contractor-merion-station-pa/",
    to: "/service-areas/siding-contractor-lower-merion-pa/",
  },
  {
    from: "/services/siding-contractor-lancaster-pa/gutter-installation-merion-station-pa/",
    to: "/service-areas/gutter-installation-lower-merion-pa/",
  },
];
